import React, { useMemo, useState } from 'react';
import { Box, Checkbox, Menu, MenuItem } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DosenTooltip from '../../../../../../components/ui/DosenTooltip';
import InfoTip from '../../../../../../components/ui/InfoTip';
import useCapabilityRelease from '../../../../../../hooks/useCapabilityRelease';
import useLearningGaps from '../../../../../../hooks/useLearningGaps';
import { useProgramContext } from '../../../../../../hooks/useProgramContext';
import { useAppSelector } from '../../../../../../redux/hooks';
import { useCapabilityInsights } from '../../../../../../queries/useCapability';
import { useLearningGapsData } from '../learning-lifecycle/useLearningGapsData';
import { useCapabilityRoles } from '../../../capability-setup/useCapabilityRoles';
import LevelMatrix from './insights/LevelMatrix';
import {
  ACTIVITY_COLUMNS,
  activityMatrix,
  capabilityColumns,
  levelMatrix,
  NOT_ASSESSED,
  NOT_OBSERVED,
  peopleFromApi,
  withDosenLevels,
  type RowsBy
} from './insights/insightsData';
import ProgramInsightsV3 from './ProgramInsightsV3';
import { MOBILE_SX, useIsMobileApp } from '../../../../../../mobile/mobileApp';

type LeadTab = 'capability' | 'activity' | 'behavior';
type InsightsTab = LeadTab | 'training';
interface Team {
  id: string;
  name: string;
}
/** What the three lead tabs look at: the whole organization, or the teams picked. */
export type Scope = 'org' | string[];

const BRANDY = '#F79420';
const BRANDY_LINE = 'rgba(247, 148, 32, 0.45)';
const FONT = 'Inter, sans-serif';
const DIVIDE = { width: '1px', height: 18, flexShrink: 0, bgcolor: 'rgba(247, 148, 32, 0.4)' };
const LEAD_TABS: Array<{ key: LeadTab; label: string }> = [
  { key: 'capability', label: 'Capability' },
  { key: 'behavior', label: 'Behavior' },
  { key: 'activity', label: 'Activity' }
];
// One line on what each tab measures.
const TAB_DESCRIPTION: Record<LeadTab, string> = {
  capability: 'The level of capability your people have proven during Roleplays and Quiz Assessments.',
  behavior:
    'The level of capability management has observed in their people on the job - a translation from learning to performance.',
  activity: 'The level of activity your people have demonstrated across our five measures of activity.'
};
const TRAINING_DESCRIPTION = 'Engagement metrics per training.';
// What each tab calls the score beside a name, and in its summary tables.
const SCORE_LABEL: Record<LeadTab, string> = {
  capability: 'Capable or better',
  activity: 'Employee Activity',
  behavior: 'Behavior Score'
};
// What the score is out of, where that is counted.
const ASSESSED_LABEL: Partial<Record<LeadTab, string>> = { capability: 'Assessed', behavior: 'Observed' };
const NO_DATA = 'Results for the selected teams will appear here as soon as people complete an Assessment or Training.';

export const TabButton: React.FC<{ label: string; active: boolean; size: number; onClick: () => void }> = ({
  label,
  active,
  size,
  onClick
}) => (
  <Box
    component="button"
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    sx={{
      p: 0,
      pb: '3px',
      border: 'none',
      borderBottom: `2px solid ${active ? BRANDY : 'transparent'}`,
      bgcolor: 'transparent',
      cursor: 'pointer',
      fontFamily: FONT,
      fontSize: size,
      fontWeight: 700,
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      color: active ? '#FDFDFD' : '#565656',
      '&:hover': { color: active ? '#FDFDFD' : '#B6B6B6' }
    }}
  >
    {label}
  </Box>
);

/**
 * The filter the three lead tabs share: the whole Organization, or one or more
 * teams. At least one team stays picked.
 */
export const ScopeFilter: React.FC<{
  teams: Team[];
  scope: Scope;
  onChange: (next: Scope) => void;
  /** Whether the whole Organization can be picked (Insights). Operations works team by team. */
  withOrganization?: boolean;
  /** What one option is called in the label ("All Roles" on Hiring). */
  noun?: string;
}> = ({ teams, scope, onChange, withOrganization = true, noun = 'Team' }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const picked = scope === 'org' ? [] : teams.filter((t) => scope.includes(t.id));
  const allPicked = scope !== 'org' && teams.length > 1 && picked.length === teams.length;
  const label =
    scope === 'org'
      ? 'Organization'
      : allPicked
      ? `All ${noun}s`
      : picked.length === 1
      ? picked[0].name
      : `${picked.length} ${noun}s`;
  const toggle = (id: string) => {
    if (scope === 'org') {
      onChange([id]);
      return;
    }
    const next = scope.includes(id) ? scope.filter((t) => t !== id) : [...scope, id];
    if (next.length > 0) onChange(next);
  };
  const itemSx = { fontFamily: FONT, fontSize: 13, color: '#FDFDFD', py: '4px', pl: '6px', pr: '14px', gap: '2px' };
  const boxSx = { p: '4px', color: '#565656', '&.Mui-checked': { color: BRANDY } };
  return (
    <>
      <Box
        component="button"
        type="button"
        aria-haspopup="menu"
        aria-label={`Showing ${label}. Change the ${noun.toLowerCase()}s shown`}
        onClick={(e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
        sx={{
          height: 30,
          maxWidth: 240,
          pl: '12px',
          pr: '6px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          border: `1px solid ${BRANDY_LINE}`,
          borderRadius: '4px',
          bgcolor: 'rgba(247, 148, 32, 0.06)',
          color: '#FDFDFD',
          fontFamily: FONT,
          fontSize: 12.5,
          fontWeight: 600,
          cursor: 'pointer',
          '&:hover': { borderColor: BRANDY },
          // Phone: the whole label, wrapping onto a second line if a team's name needs it.
          [MOBILE_SX]: { maxWidth: '100%', height: 'auto', minHeight: 36, py: '6px', fontSize: 13, textAlign: 'left' }
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            [MOBILE_SX]: { whiteSpace: 'normal' }
          }}
        >
          {label}
        </Box>
        <ExpandMoreIcon sx={{ fontSize: 18, flexShrink: 0 }} />
      </Box>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{
          sx: {
            mt: '6px',
            minWidth: 220,
            maxHeight: 360,
            bgcolor: '#1C1C1C',
            backgroundImage: 'none',
            border: '1px solid #444444'
          }
        }}
      >
        {teams.map((team) => (
          <MenuItem key={team.id} onClick={() => toggle(team.id)} sx={itemSx}>
            <Checkbox checked={scope !== 'org' && scope.includes(team.id)} size="small" sx={boxSx} />
            {team.name}
          </MenuItem>
        ))}
        {withOrganization && <Box sx={{ height: '1px', my: '4px', bgcolor: '#2A2A2A' }} />}
        {withOrganization && (
          <MenuItem onClick={() => onChange('org')} sx={itemSx}>
            <Checkbox checked={scope === 'org'} size="small" sx={boxSx} />
            Organization
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

/**
 * Capability, Activity or Behavior for what is in view, as one table filling
 * the page: rows by employee, role or team (one row for the org when the
 * Organization is picked); the employees and scores each row covers and its
 * score; then a column per capability or measure.
 */
const LeadInsights: React.FC<{ tab: LeadTab; teams: Team[]; wholeOrg: boolean }> = ({ tab, teams, wholeOrg }) => {
  const orgName = useAppSelector((s) => (s.orgDetails.data as { name?: string } | undefined)?.name) || 'Organization';
  const [viewBy, setViewBy] = useState<RowsBy>('team');
  const rowsBy: RowsBy = wholeOrg ? 'org' : viewBy;

  // The teams in view: everyone in them, their levels (Capability), the levels their managers
  // observed (Behavior) and their Activity measures, from GET /v3/admin/capability/insights.
  const { data, isLoading } = useCapabilityInsights(
    teams.map((t) => t.id),
    teams.length > 0
  );
  const pairs = useMemo(() => data?.pairs ?? [], [data]);
  const people = useMemo(() => peopleFromApi(data?.people ?? []), [data]);
  const observed = useMemo(() => data?.observed ?? [], [data]);
  const activity = useMemo(() => new Map((data?.activity ?? []).map((a) => [a.id, a.measures])), [data]);
  const columns = useMemo(
    () => (tab === 'activity' ? ACTIVITY_COLUMNS : capabilityColumns(pairs, teams)),
    [tab, pairs, teams]
  );
  const rows = useMemo(() => {
    if (tab === 'activity') return activityMatrix(rowsBy, people, orgName, activity);
    const dosen = levelMatrix(rowsBy, pairs, people, columns, orgName, NOT_ASSESSED);
    // Behavior: what was observed, each cell carrying what Dosen has for it to compare on hover.
    return tab === 'behavior'
      ? withDosenLevels(levelMatrix(rowsBy, observed, people, columns, orgName, NOT_OBSERVED), dosen)
      : dosen;
  }, [tab, rowsBy, people, orgName, observed, pairs, columns, activity]);

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        p: '16px',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#1C1C1C',
        border: `1px solid ${BRANDY_LINE}`,
        borderRadius: '8px',
        boxShadow: '0 0 28px rgba(247, 148, 32, 0.07)',
        // The phone's content area scrolls the cards.
        [MOBILE_SX]: { overflow: 'visible', p: '12px' }
      }}
    >
      <LevelMatrix
        rowsBy={rowsBy}
        locked={wholeOrg}
        onRowsByChange={setViewBy}
        scoreLabel={SCORE_LABEL[tab]}
        assessedLabel={ASSESSED_LABEL[tab]}
        columns={columns}
        rows={rows}
        empty={NO_DATA}
        loading={teams.length > 0 && isLoading}
        // A % opens the dashboard's breakdown of the scores behind it.
        breakdown={
          tab === 'activity'
            ? undefined
            : { teams, resultLabel: tab === 'behavior' ? 'Behavior Score' : 'Capable or better' }
        }
      />
    </Box>
  );
};

/**
 * Insights with the Capability release. One bar across the top: Capability |
 * Behavior | Activity and the filter the three share (the Organization, or
 * teams) on the left; Training, the Insights page as it was with its own
 * program filter, in smaller type on the right behind a divide.
 */
const ReleaseInsights: React.FC = () => {
  const [tab, setTab] = useState<InsightsTab>('capability');
  const { orgId, isPlatformAdmin } = useProgramContext();
  const learningGaps = useLearningGaps();
  // The org's teams, scoped by the BE to what this admin may see (as on the dashboard).
  const gapsData = useLearningGapsData(learningGaps, isPlatformAdmin ? orgId : undefined);
  // Every team in the org, whoever is looking: a team manager sees all of them here, not only
  // their own. Capability Setup lists them all; the Learning Gaps teams stand in where it cannot.
  const setup = useCapabilityRoles(orgId, isPlatformAdmin);
  const teamOptions = useMemo(() => {
    const all = setup.teams.length > 0 ? setup.teams : gapsData.teams;
    return all.map((t) => ({ id: t.id, name: t.name }));
  }, [setup.teams, gapsData.teams]);
  // On arrival every team is picked (so View By is open, by team); null until the admin chooses otherwise.
  const [chosen, setScope] = useState<Scope | null>(null);
  const scope: Scope = useMemo(() => chosen ?? teamOptions.map((t) => t.id), [chosen, teamOptions]);
  const teams = useMemo(
    () => (scope === 'org' ? teamOptions : teamOptions.filter((t) => scope.includes(t.id))),
    [scope, teamOptions]
  );
  const lead = tab !== 'training';
  // The phone: the four tabs as one sideways-scrolling row; what a tab measures (the (i) on
  // desktop) and the teams filter on their own row beneath it.
  const isMobileApp = useIsMobileApp();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        bgcolor: '#0F0F0F',
        [MOBILE_SX]: { height: 'auto', overflow: 'visible' }
      }}
    >
      <Box sx={{ flexShrink: 0, px: '25px', pt: '20px', [MOBILE_SX]: { px: 0, pt: 0 } }}>
        <Box
          role="tablist"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            p: '10px 14px',
            bgcolor: '#1C1C1C',
            border: '1px solid #2A2A2A',
            borderRadius: '8px',
            [MOBILE_SX]: {
              gap: '8px',
              px: '12px',
              minHeight: 48,
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' }
            }
          }}
        >
          {/* The three tabs, then the filter that is theirs alone. */}
          {LEAD_TABS.map((item, i) => (
            <React.Fragment key={item.key}>
              {i > 0 && <Box sx={DIVIDE} />}
              <TabButton
                label={item.label}
                size={isMobileApp ? 14 : 17}
                active={tab === item.key}
                onClick={() => setTab(item.key)}
              />
              {tab === item.key && !isMobileApp && (
                <InfoTip title={TAB_DESCRIPTION[item.key]} placement="bottom-start" />
              )}
            </React.Fragment>
          ))}
          {!isMobileApp && (
            <Box sx={{ opacity: lead ? 1 : 0.45 }}>
              <ScopeFilter teams={teamOptions} scope={scope} onChange={setScope} />
            </Box>
          )}
          <Box sx={{ flex: 1, [MOBILE_SX]: { display: 'none' } }} />
          {/* Training, across the bar behind its own divide, with its own program filter on its page. */}
          <Box sx={DIVIDE} />
          {/* The phone says it under the tabs, as for the others. */}
          <DosenTooltip title={isMobileApp ? '' : TRAINING_DESCRIPTION} placement="bottom">
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <TabButton
                label="Training"
                size={isMobileApp ? 12 : 13}
                active={tab === 'training'}
                onClick={() => setTab('training')}
              />
            </Box>
          </DosenTooltip>
        </Box>
        {isMobileApp && (
          <Box sx={{ mt: '12px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px' }}>
            <Box sx={{ fontFamily: FONT, fontSize: 13, lineHeight: 1.45, color: '#B6B6B6' }}>
              {lead ? TAB_DESCRIPTION[tab] : TRAINING_DESCRIPTION}
            </Box>
            {lead && <ScopeFilter teams={teamOptions} scope={scope} onChange={setScope} />}
          </Box>
        )}
      </Box>
      {tab === 'training' ? (
        <Box role="tabpanel" sx={{ flex: 1, minHeight: 0, [MOBILE_SX]: { flex: '0 0 auto', mt: '12px' } }}>
          <ProgramInsightsV3 />
        </Box>
      ) : (
        <Box
          role="tabpanel"
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            px: '25px',
            pt: '12px',
            pb: '20px',
            [MOBILE_SX]: { flex: '0 0 auto', px: 0, pb: 0 }
          }}
        >
          <LeadInsights tab={tab} teams={teams} wholeOrg={scope === 'org'} />
        </Box>
      )}
    </Box>
  );
};

/** Insights: the tabbed page with the Capability release, the page as it was without it. */
const InsightsPage: React.FC = () => (useCapabilityRelease() ? <ReleaseInsights /> : <ProgramInsightsV3 />);

export default InsightsPage;
